import { useState, type JSX } from 'react';
import { moedaBr } from '../estado/gerador.js';
import { rotuloTabela } from '../lib/formato.js';
import { ROTULO_CAMPO, type Preview, type ResumoPorTabela } from '../tipos/importacao.js';
import './PreviewImportacao.css';

/**
 * O que muda se esta tabela for efetivada — Story 8.4.
 *
 * Quatro listas, e a ordem delas é intencional: primeiro o que **muda preço**
 * (divergências e ficha), depois o que muda **cobertura** (novos, sem preço).
 * Quem confere uma tabela nova está procurando o valor que pulou, não o
 * produto que entrou.
 *
 * As divergências vêm ordenadas pela **maior variação em módulo**: um salto de
 * 40% num item é o que precisa ser visto antes de rolar a lista.
 */
export function PreviewImportacao({ preview }: { preview: Preview }): JSX.Element {
  /*
   * "Nada muda" tem de ser VERDADE — correção da UX.3.
   *
   * Antes, esta soma ignorava adição e remoção de preço, porque o backend nem
   * as contava. Uma tabela inteira podia entrar com os cinco contadores em zero
   * e a tela afirmava "Efetivar não mudaria nada". Qualquer diferença
   * estrutural agora quebra o silêncio.
   */
  /*
   * S11 — rodapé NOVO nasce vendido por caixa e faturaria a caixa inteira
   * (36 a 67,2 m) até alguém configurar a barra. O aviso é obrigatório: com
   * ele pendente, "nada muda" seria mentira.
   */
  const rodapesPorCaixa = preview.rodapesNovosPorCaixa ?? [];

  const semNada =
    preview.resumo.divergencias === 0 &&
    preview.resumo.mudancasFicha === 0 &&
    preview.resumo.novos === 0 &&
    preview.resumo.removidos === 0 &&
    preview.resumo.semPreco === 0 &&
    preview.resumo.precosNovos === 0 &&
    preview.resumo.precosRemovidos === 0 &&
    rodapesPorCaixa.length === 0;

  if (semNada) {
    return (
      <p className="nota nota--ok" role="status">
        Nenhuma diferença em relação à tabela em vigor: mesmos produtos, mesmos preços e mesmas
        fichas. Efetivar não mudaria nada.
      </p>
    );
  }

  return (
    <div className="preview">
      <div className="preview__resumo">
        <Cartela rotulo="Preços alterados" valor={preview.resumo.divergencias} destaque />
        <Cartela rotulo="Preços novos" valor={preview.resumo.precosNovos} adicao />
        <Cartela rotulo="Preços removidos" valor={preview.resumo.precosRemovidos} alerta />
        <Cartela rotulo="Fichas alteradas" valor={preview.resumo.mudancasFicha} destaque />
        <Cartela rotulo="Produtos novos" valor={preview.resumo.novos} />
        <Cartela rotulo="Sem preço" valor={preview.resumo.semPreco} alerta />
        <Cartela rotulo="Removidos" valor={preview.resumo.removidos} alerta />
      </div>

      {preview.porTabela.length > 0 && <PorTabela linhas={preview.porTabela} />}

      {rodapesPorCaixa.length > 0 && (
        <Secao
          titulo="Rodapés novos vendidos por caixa"
          vazio=""
          total={rodapesPorCaixa.length}
          aviso="Rodapé novo vendido por caixa: configurar a barra. Até lá, ele fatura a caixa inteira (36 a 67,2 m), e não barras de 2,40 m."
        >
          <ListaRefs refs={rodapesPorCaixa} />
        </Secao>
      )}

      {preview.comparadaCom === null && (
        <p className="nota">
          Esta é a <strong>primeira carga</strong> do tenant — não há tabela anterior para comparar,
          então tudo aparece como novo.
        </p>
      )}

      <Secao
        titulo="Preços que mudam"
        vazio="Nenhum preço mudou."
        total={preview.resumo.divergencias}
      >
        <table className="tabela">
          <thead>
            <tr>
              <th scope="col">Referência</th>
              <th scope="col">Tabela</th>
              <th scope="col">Fonte</th>
              <th scope="col" className="num">
                De
              </th>
              <th scope="col" className="num">
                Para
              </th>
              <th scope="col" className="num">
                Variação
              </th>
            </tr>
          </thead>
          <tbody>
            {[...preview.divergencias]
              .sort(
                (a, b) => Math.abs(Number(b.variacaoPercent)) - Math.abs(Number(a.variacaoPercent)),
              )
              .map((d) => (
                <tr key={`${d.referencia}-${d.tabela}-${d.priceSourceKey}`}>
                  <td className="preview__ref">{d.referencia}</td>
                  <td>{rotuloTabela(d.tabela)}</td>
                  <td className="preview__fonte">{d.priceSourceKey}</td>
                  <td className="num">{moedaBr(d.valorAnterior)}</td>
                  <td className="num">{moedaBr(d.valorNovo)}</td>
                  <td className={`num ${classeVariacao(d.variacaoPercent)}`}>
                    {d.variacaoPercent === 'n/d' ? '—' : `${d.variacaoPercent}%`}
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </Secao>

      <Secao
        titulo="Fichas de produto que mudam"
        vazio="Nenhuma ficha muda."
        total={preview.resumo.mudancasFicha}
        aviso="Peso e medida por caixa entram no cálculo do frete e do arredondamento por caixa fechada — mudá-los altera o preço final de todo orçamento futuro dessas referências."
      >
        <table className="tabela">
          <thead>
            <tr>
              <th scope="col">Referência</th>
              <th scope="col">Campo</th>
              <th scope="col" className="num">
                De
              </th>
              <th scope="col" className="num">
                Para
              </th>
            </tr>
          </thead>
          <tbody>
            {preview.mudancasFicha.map((m) => (
              <tr key={`${m.referencia}-${m.campo}`}>
                <td className="preview__ref">{m.referencia}</td>
                <td>{ROTULO_CAMPO[m.campo]}</td>
                <td className="num">{m.de ?? '—'}</td>
                <td className="num preview__para">{m.para ?? '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Secao>

      <Secao
        titulo="Produtos que ficam sem preço"
        vazio="Nenhum produto fica sem preço."
        total={preview.resumo.semPreco}
        aviso="Estes produtos existem no catálogo e a nova tabela não os cobre. Efetivando, eles deixam de ser orçáveis."
      >
        <ListaRefs refs={preview.semPreco} />
      </Secao>

      <Secao
        titulo="Preços novos (adições)"
        vazio="Nenhum preço novo."
        total={preview.resumo.precosNovos}
      >
        <TabelaPrecos
          linhas={preview.precosNovos.map((p) => ({
            referencia: p.referencia,
            tabela: p.tabela,
            priceSourceKey: p.priceSourceKey,
            valor: p.valor,
          }))}
          rotuloValor="Preço"
          total={preview.resumo.precosNovos}
        />
      </Secao>

      <Secao
        titulo="Preços removidos"
        vazio="Nenhum preço removido."
        total={preview.resumo.precosRemovidos}
        aviso="Estes produtos continuam no catálogo, mas deixam de ter preço nesta tabela. Efetivando, eles somem daquele quadrante sem sair da lista de produtos."
      >
        <TabelaPrecos
          linhas={preview.precosRemovidos.map((p) => ({
            referencia: p.referencia,
            tabela: p.tabela,
            priceSourceKey: p.priceSourceKey,
            valor: p.valorAnterior,
          }))}
          rotuloValor="Preço que sai"
          total={preview.resumo.precosRemovidos}
        />
      </Secao>

      <Secao titulo="Produtos novos" vazio="Nenhum produto novo." total={preview.resumo.novos}>
        <ListaRefs refs={preview.novos} />
      </Secao>
    </div>
  );
}

function classeVariacao(v: string): string {
  if (v === 'n/d') return '';
  return v.startsWith('-') ? 'preview__baixa' : 'preview__alta';
}

function Cartela({
  rotulo,
  valor,
  destaque,
  alerta,
  adicao,
}: {
  rotulo: string;
  valor: number;
  destaque?: boolean;
  alerta?: boolean;
  /** ADIÇÃO — visualmente separada de alteração e de remoção. */
  adicao?: boolean;
}): JSX.Element {
  const classe =
    valor === 0
      ? 'cartela cartela--zero'
      : alerta === true
        ? 'cartela cartela--alerta'
        : adicao === true
          ? 'cartela cartela--adicao'
          : destaque === true
            ? 'cartela cartela--destaque'
            : 'cartela';
  return (
    <div className={classe}>
      <span className="cartela__valor">{valor}</span>
      <span className="cartela__rotulo">{rotulo}</span>
    </div>
  );
}

/**
 * Seção recolhível.
 *
 * Com o catálogo completo, "produtos novos" tem ~300 linhas na primeira carga —
 * aberto por padrão, isso enterraria as três listas que realmente importam.
 * Abre sozinha só quando é pequena o bastante para caber na tela.
 */
function Secao({
  titulo,
  vazio,
  total,
  aviso,
  children,
}: {
  titulo: string;
  vazio: string;
  total: number;
  aviso?: string;
  children: JSX.Element;
}): JSX.Element {
  const [aberta, setAberta] = useState(total > 0 && total <= 20);

  if (total === 0) {
    return (
      <section className="preview__secao preview__secao--vazia">
        <h3 className="preview__titulo">{titulo}</h3>
        <p className="preview__vazio">{vazio}</p>
      </section>
    );
  }

  return (
    <section className="preview__secao">
      <button
        type="button"
        className="preview__cabecalho"
        aria-expanded={aberta}
        onClick={() => {
          setAberta((v) => !v);
        }}
      >
        <h3 className="preview__titulo">
          {titulo} <span className="preview__contagem">{total}</span>
        </h3>
        <span className="preview__seta">{aberta ? 'ocultar' : 'ver'}</span>
      </button>

      {aviso !== undefined && <p className="preview__aviso">{aviso}</p>}
      {aberta && <div className="preview__conteudo">{children}</div>}
    </section>
  );
}

function ListaRefs({ refs }: { refs: string[] }): JSX.Element {
  return (
    <ul className="preview__refs">
      {refs.map((r) => (
        <li key={r} className="preview__ref-chip">
          {r}
        </li>
      ))}
    </ul>
  );
}

/**
 * O diff somado por tabela.
 *
 * É a linha que responde a pergunta de quem vai efetivar: "entrou a coluna X
 * com N preços?". A lista item a item não responde isso — 192 linhas soltas
 * não dizem que são todas do mesmo quadrante.
 */
function PorTabela({ linhas }: { linhas: ResumoPorTabela[] }): JSX.Element {
  return (
    <div className="preview__portabela">
      <h3 className="preview__titulo">O que muda em cada tabela</h3>
      <table className="tabela">
        <thead>
          <tr>
            <th scope="col">Tabela</th>
            <th scope="col">Alterados</th>
            <th scope="col">Novos</th>
            <th scope="col">Removidos</th>
          </tr>
        </thead>
        <tbody>
          {linhas.map((l) => (
            <tr key={l.tabela}>
              <td>
                {rotuloTabela(l.tabela)}
                {l.inteiraNova && <strong className="etiqueta etiqueta--ok"> tabela nova</strong>}
                {l.inteiraRemovida && (
                  <strong className="etiqueta etiqueta--aviso"> tabela removida</strong>
                )}
              </td>
              <td>{l.alterados > 0 ? l.alterados : '—'}</td>
              <td className={l.novos > 0 ? 'preview__adicao' : ''}>
                {l.novos > 0 ? l.novos : '—'}
              </td>
              <td className={l.removidos > 0 ? 'preview__remocao' : ''}>
                {l.removidos > 0 ? l.removidos : '—'}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** Lista de preços por chave completa — usada por adições e remoções. */
function TabelaPrecos({
  linhas,
  rotuloValor,
  total,
}: {
  linhas: { referencia: string; tabela: string; priceSourceKey: string; valor: string }[];
  rotuloValor: string;
  total: number;
}): JSX.Element {
  return (
    <>
      <table className="tabela">
        <thead>
          <tr>
            <th scope="col">Referência</th>
            <th scope="col">Tabela</th>
            <th scope="col">Coluna</th>
            <th scope="col">{rotuloValor}</th>
          </tr>
        </thead>
        <tbody>
          {linhas.map((l) => (
            <tr key={`${l.referencia}|${l.tabela}|${l.priceSourceKey}`}>
              <td>{l.referencia}</td>
              <td>{rotuloTabela(l.tabela)}</td>
              <td>{l.priceSourceKey}</td>
              <td>R$ {moedaBr(l.valor)}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {total > linhas.length && (
        <p className="nota">
          Mostrando {linhas.length} de {total}. A contagem por tabela, acima, é completa.
        </p>
      )}
    </>
  );
}
